import LoadingButton from '@/common/components/base/button/loadingButton';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { insuranceOption } from '@/common/queries/selections/insurance/insuranceOption';
import { getInsurancePaymentByOption } from '@/common/queries/selections/insurancePaymentBy/insurancePaymentByOption';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import convertTimestampToShamsi from '@/common/utils/convertTimestampToShamsi';
import { INSURANCE_NUMBER_RULE } from '@/common/utils/rules/insurance.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import {
  validateNotFutureDate,
  validateNotPastDate,
} from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import { getCulpritPropertyInsuranceOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getCulpritPropertyInsurance/getCulpritPropertyInsuranceOptions';
import { PatchCulpritPropertyInsuranceOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchCulpritPropertyInsurance/patchCulpritPropertyInsuranceOptions';
import { PropertyInsuranceFormType } from '@/modules/adjusterReportModules/IncidentParticipants/types/PropertyInsurance.type';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Flex } from 'styled-system/jsx';

const EditCulpritPropertyInsurance = () => {
  const methods = useForm<PropertyInsuranceFormType>({ mode: 'onChange' });
  const { id } = useParams();
  const [name, setName] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: insurances, isPending } = useQuery(
    insuranceOption({ name: name }),
  );
  const { data: paymentBy, isPending: paymentByPending } = useQuery(
    getInsurancePaymentByOption(),
  );
  const { data: culpritInsuranceData, isPending: culpritPending } = useQuery(
    getCulpritPropertyInsuranceOptions({ id: +id! }),
  );

  const { mutate, isPending: formIsPending } = useMutation(
    PatchCulpritPropertyInsuranceOptions(),
  );
  const submitHandler = (data: PropertyInsuranceFormType) => {
    mutate(
      {
        id: +id!,
        formData: {
          insurance_coupon_number:
            paymentByWatcher?.value === 1 ? data?.insurance_coupon_number : '0',
          insurance_expiry_date: convertPersianToGregorian(
            data?.insurance_expiry_date!,
          ),
          insurance_expiry_date_last_year: convertPersianToGregorian(
            data?.insurance_expiry_date_last_year!,
          ),
          insurance_injury_commitment_max:
            data?.insurance_injury_commitment_max &&
            data?.insurance_injury_commitment_max.toString() !== ''
              ? data?.insurance_injury_commitment_max
              : 0,
          insurance_number: data?.insurance_number,
          insurance_passenger_commitment_max:
            data?.insurance_passenger_commitment_max &&
            data?.insurance_passenger_commitment_max.toString() !== ''
              ? data?.insurance_passenger_commitment_max
              : 0,
          insurance_payment_by: data?.insurance_payment_by?.value,
          insurance_property_commitment_max:
            data?.insurance_property_commitment_max &&
            data?.insurance_property_commitment_max.toString() !== ''
              ? data?.insurance_property_commitment_max
              : 0,
          insurance_start_date: convertPersianToGregorian(
            data?.insurance_start_date!,
          ),
        },
      },
      {
        onSuccess: () => {
          navigate(-1);
          queryClient.invalidateQueries({
            queryKey: getCulpritPropertyInsuranceOptions({ id: +id! }).queryKey,
          });
        },
      },
    );
  };

  const paymentByWatcher = useWatch({
    control: methods.control,
    name: 'insurance_payment_by',
  });

  useEffect(() => {
    if (culpritInsuranceData && paymentBy) {
      methods.reset({
        insurance: {
          label: culpritInsuranceData?.insurance?.name as string,
          value: culpritInsuranceData?.insurance?.id as number,
        },
        insurance_number: culpritInsuranceData?.insurance_number,
        insurance_coupon_number:
          culpritInsuranceData?.insurance_coupon_number ?? null,
        insurance_expiry_date: culpritInsuranceData?.insurance_expiry_date
          ? convertTimestampToShamsi(
              culpritInsuranceData?.insurance_expiry_date,
            )
          : null,
        insurance_expiry_date_last_year:
          culpritInsuranceData?.insurance_expiry_date_last_year
            ? convertTimestampToShamsi(
                culpritInsuranceData?.insurance_expiry_date_last_year,
              )
            : null,
        insurance_injury_commitment_max:
          culpritInsuranceData?.insurance_injury_commitment_max ?? 0,
        insurance_passenger_commitment_max:
          culpritInsuranceData?.insurance_passenger_commitment_max ?? 0,
        insurance_payment_by: culpritInsuranceData?.insurance_payment_by
          ? {
              label: paymentBy?.find(
                (item) =>
                  item?.fa_const ===
                  culpritInsuranceData?.insurance_payment_by?.fa_const!,
              )?.fa_const,
              value: paymentBy?.find(
                (item) =>
                  item?.fa_const ===
                  culpritInsuranceData?.insurance_payment_by?.fa_const!,
              )?.const,
            }
          : null,
        insurance_start_date: culpritInsuranceData?.insurance_start_date
          ? convertTimestampToShamsi(culpritInsuranceData?.insurance_start_date)
          : null,
        insurance_property_commitment_max:
          culpritInsuranceData?.insurance_property_commitment_max ?? 0,
      });
    }
  }, [culpritInsuranceData, paymentBy]);

  const startDateWatcher = useWatch({
    control: methods?.control,
    name: 'insurance_start_date',
  });
  const expiryDateWatcher = useWatch({
    control: methods?.control,
    name: 'insurance_expiry_date',
  });

  useEffect(() => {
    if (expiryDateWatcher) {
      methods.trigger('insurance_start_date');
      methods.trigger('insurance_expiry_date');
    }
  }, [expiryDateWatcher]);

  return (
    <FormProvider {...methods}>
      <Flex direction="column" gap="1rem" pt={24} mb={24}>
        <ModalInput
          name="insurance"
          placeholder="انتخاب کنید"
          label="بیمه‌گر"
          title="بیمه‌گر"
          disabled
          loading={isPending}
          searchOptions={{
            onChange: (e) => {
              setName(e);
            },
          }}
          options={insurances?.map((insurance) => ({
            label: insurance?.name as string,
            value: insurance?.id as number,
          }))}
        />
        <TextInput
          placeholder="شماره بیمه نامه را وارد کنید"
          label="شماره بیمه نامه"
          name="insurance_number"
          max={25}
          rules={{
            required: { message: requiredText, value: true },
            ...INSURANCE_NUMBER_RULE,
          }}
        />
        <Flex direction="row">
          <DateInput
            label="تاریخ شروع"
            buttonText="ثبت"
            modalLabel="تاریخ شروع"
            name="insurance_start_date"
            isDefaultValueEmpty={true}
            rules={{
              required: requiredText,
              validate: {
                notFuture: validateNotFutureDate,
                notExpiryDate: () => {
                  if (!expiryDateWatcher) return true;
                  if (!startDateWatcher) return true;

                  try {
                    const expiry = convertPersianToGregorian(expiryDateWatcher);
                    const start = convertPersianToGregorian(startDateWatcher);
                    if (expiry < start) {
                      return ' تاریخ شروع نمی‌تواند بعد از تاریخ انقضا باشد';
                    }
                    return true;
                  } catch (err) {
                    return 'خطا در تبدیل تاریخ';
                  }
                },
              },
            }}
            className={css({
              borderLeftRadius: '0px',
            })}
          />
          <DateInput
            label="تاریخ انقضا"
            buttonText="ثبت"
            modalLabel="تاریخ انقضا"
            isDefaultValueEmpty={true}
            rules={{
              required: requiredText,
              validate: {
                notPast: validateNotPastDate,
                notExpiryDate: () => {
                  if (!expiryDateWatcher) return true;
                  if (!startDateWatcher) return true;

                  try {
                    const expiry = convertPersianToGregorian(expiryDateWatcher);
                    const start = convertPersianToGregorian(startDateWatcher);
                    if (expiry < start) {
                      return 'تاریخ انقضا نمی‌تواند قبل از تاریخ شروع باشد';
                    }
                    return true;
                  } catch (err) {
                    return 'خطا در تبدیل تاریخ';
                  }
                },
              },
            }}
            name="insurance_expiry_date"
            className={css({
              borderRightRadius: '0px',
            })}
          />
        </Flex>
        <ModalInput
          name="insurance_payment_by"
          placeholder="انتخاب کنید"
          label=" پرداخت از محل"
          title="پرداخت از محل "
          loading={paymentByPending}
          rules={{ required: { message: requiredText, value: true } }}
          options={paymentBy?.map((payment) => ({
            label: payment?.fa_const as string,
            value: payment?.const as number,
          }))}
        />
        {paymentByWatcher?.value === 1 && (
          <TextInput
            name="insurance_coupon_number"
            label="شماره کوپن"
            placeholder="شماره کوپن"
            rules={{
              pattern: {
                value: /^[0-9]+$/,
                message: 'باید رقم وارد کنید',
              },
              required: {
                message: requiredText,
                value: paymentByWatcher?.value === 1,
              },
            }}
          />
        )}
        <DateInput
          label="تاریخ انقضا بیمه نامه سال قبل"
          buttonText="ثبت"
          modalLabel="تاریخ انقضا بیمه نامه سال قبل"
          rules={{
            required: { message: requiredText, value: true },
            validate: validateNotFutureDate,
          }}
          name="insurance_expiry_date_last_year"
          isDefaultValueEmpty={true}
        />
        <TextInput
          name="insurance_property_commitment_max"
          label="سقف تعهدات مالی"
          placeholder="سقف تعهدات مالی"
          optionalLabel
          rules={{
            pattern: {
              value: /^[0-9]+$/,
              message: 'باید رقم وارد کنید',
            },
            // required: { message: requiredText, value: true },
          }}
        />
        <TextInput
          name="insurance_injury_commitment_max"
          label="سقف تعهدات جانی"
          placeholder="سقف تعهدات جانی"
          optionalLabel
          rules={{
            pattern: {
              value: /^[0-9]+$/,
              message: 'باید رقم وارد کنید',
            },
          }}
        />
        <TextInput
          name="insurance_passenger_commitment_max"
          label="سقف تعهدات سرنشین"
          placeholder="سقف تعهدات سرنشین"
          optionalLabel
          rules={{
            pattern: {
              value: /^[0-9]+$/,
              message: 'باید رقم وارد کنید',
            },
          }}
        />
        <LoadingButton
          onClick={methods.handleSubmit(submitHandler)}
          type="submit"
          width="100%"
          marginTop="6px"
          loading={formIsPending}
          disabled={
            culpritPending ||
            !methods.formState.isValid ||
            methods.formState.isSubmitting
          }
        >
          ثبت
        </LoadingButton>
      </Flex>
    </FormProvider>
  );
};

export default EditCulpritPropertyInsurance;
